import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';
import {
  MdPerson,
  MdExpandMore,
  MdExpandLess,
  MdSchool,
  MdCalendarToday,
  MdPhone,
  MdCheckCircle,
  MdWarning,
  MdRefresh,
  MdComment,
  MdFiberManualRecord,
  MdHistory,
  MdAdd,
  MdDelete,
  MdGroup,
  MdSearch,
} from 'react-icons/md';
import Select from 'react-select';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_META = {
  pending: {
    label: 'Pending',
    dot: 'bg-amber-400',
    badge: 'border border-amber-200 bg-amber-50 text-amber-700',
  },
  'in followup': {
    label: 'In Follow-up',
    dot: 'bg-sky-400',
    badge: 'border border-sky-200 bg-sky-50 text-sky-700',
  },
  'planned to rejoin': {
    label: 'Planned to Rejoin',
    dot: 'bg-violet-400',
    badge: 'border border-violet-200 bg-violet-50 text-violet-700',
  },
  reactivated: {
    label: 'Reactivated',
    dot: 'bg-emerald-400',
    badge: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  'permanently inactive': {
    label: 'Permanently Inactive',
    dot: 'bg-red-400',
    badge: 'border border-red-200 bg-red-50 text-red-700',
  },
  transferred: {
    label: 'Transferred',
    dot: 'bg-indigo-400',
    badge: 'border border-indigo-200 bg-indigo-50 text-indigo-700',
  },
  'no answer': {
    label: 'No Answer',
    dot: 'bg-slate-400',
    badge: 'border border-slate-200 bg-slate-100 text-slate-600',
  },
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, { label }]) => ({ value, label }));

const GOAL_OPTIONS = [
  { value: 'reactivate', label: 'Reactivate' },
  { value: 'confirm inactive', label: 'Confirm Inactive' },
  { value: 'transfer to another branch', label: 'Transfer to Another Branch' },
  { value: 'transfer to another course', label: 'Transfer to Another Course' },
  { value: 'other', label: 'Other' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initialsFromName(name) {
  if (!name) return 'NA';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ src, name, gender, size = 40 }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = initialsFromName(name);
  const isMale = gender && ['male', 'm'].includes(String(gender).toLowerCase());
  const bg = isMale ? '#3b82f6' : '#ec4899';

  if (src && !imgErr) {
    return (
      <img
        src={getImageURL(src)}
        alt={name}
        onError={() => setImgErr(true)}
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold select-none"
      style={{ width: size, height: size, background: bg, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
}

function StaffAvatar({ name, size = 40 }) {
  const initials = initialsFromName(name);
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold select-none"
      style={{ width: size, height: size, background: '#4f46e5', fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, badge: 'border border-gray-200 bg-gray-50 text-gray-600', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${meta.badge}`}>
      <MdFiberManualRecord className={`text-[8px] ${meta.dot.replace('bg-', 'text-')}`} />
      {meta.label}
    </span>
  );
}

function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return val;
  }
}

function fmtDatetime(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return val;
  }
}

// Returns YYYY-MM-DD in the browser's local timezone (avoids UTC shift from server-serialised Date objects)
function toLocalDateStr(val) {
  if (!val) return null;
  const d = new Date(val);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function rsStyles() {
  return {
    control: (b, s) => ({
      ...b,
      borderRadius: 12,
      borderColor: s.isFocused ? '#818cf8' : '#e5e7eb',
      boxShadow: s.isFocused ? '0 0 0 2px #c7d2fe' : 'none',
      minHeight: 42,
      fontSize: 14,
      '&:hover': { borderColor: '#818cf8' },
    }),
    menu: (b) => ({ ...b, borderRadius: 12, zIndex: 9999 }),
    option: (b, s) => ({
      ...b,
      fontSize: 14,
      backgroundColor: s.isSelected ? '#4f46e5' : s.isFocused ? '#eef2ff' : 'white',
      color: s.isSelected ? 'white' : '#374151',
    }),
  };
}

// ─── Add Followup Modal ───────────────────────────────────────────────────────

function AddFollowupModal({ inquiry, onClose, onAdded }) {
  const [form, setForm] = useState({
    comment: '',
    comment_date: new Date().toISOString().slice(0, 10),
    next_reminder_date: '',
    next_reminder_comment: '',
    status_update: '',
    planned_rejoin_date: '',
    is_attending_classes: '',
    attending_since: '',
  });
  const [saving, setSaving] = useState(false);

  const showPlannedDate = form.status_update === 'planned to rejoin';
  const showAttendingSince = form.is_attending_classes === '1';

  const handleSave = async () => {
    if (!form.comment.trim()) {
      Swal.fire({ icon: 'warning', title: 'Comment required', text: 'Please enter a comment.' });
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.post(`/admin/inactivated-inquiry/${inquiry.inquiry_id}/followup`, {
        comment: form.comment,
        comment_date: form.comment_date || null,
        next_reminder_date: form.next_reminder_date || null,
        next_reminder_comment: form.next_reminder_comment || null,
        status_update: form.status_update || null,
        planned_rejoin_date: showPlannedDate ? (form.planned_rejoin_date || null) : null,
        is_attending_classes: form.is_attending_classes !== '' ? Number(form.is_attending_classes) : null,
        attending_since: showAttendingSince ? (form.attending_since || null) : null,
      });
      onAdded();
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to save follow-up.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Add Follow-up</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{inquiry.full_name} · {inquiry.registration_number}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <span className="text-lg font-bold">✕</span>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Comment <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={4}
              value={form.comment}
              onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))}
              placeholder="What was discussed / outcome of the follow-up..."
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Comment Date</label>
              <input type="date" value={form.comment_date} onChange={(e) => setForm((p) => ({ ...p, comment_date: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Next Reminder Date</label>
              <input type="date" value={form.next_reminder_date} onChange={(e) => setForm((p) => ({ ...p, next_reminder_date: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent" />
            </div>
          </div>
          {form.next_reminder_date && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Next Reminder Note</label>
              <input type="text" value={form.next_reminder_comment} onChange={(e) => setForm((p) => ({ ...p, next_reminder_comment: e.target.value }))}
                placeholder="What to follow up on..."
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent" />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Update Status (Optional)</label>
            <Select isClearable options={STATUS_OPTIONS} value={STATUS_OPTIONS.find((o) => o.value === form.status_update) || null}
              onChange={(o) => setForm((p) => ({ ...p, status_update: o?.value || '' }))}
              placeholder="Leave unchanged..." classNamePrefix="rs" styles={rsStyles()} />
          </div>
          {showPlannedDate && (
            <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 space-y-3">
              <p className="text-xs font-medium text-violet-700 flex items-center gap-1.5">
                <MdCalendarToday size={14} /> Student plans to rejoin
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Planned Rejoin Date</label>
                <input type="date" value={form.planned_rejoin_date} onChange={(e) => setForm((p) => ({ ...p, planned_rejoin_date: e.target.value }))}
                  className="w-full border border-violet-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent bg-white" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Is Student Attending Classes?</label>
            <div className="flex gap-3">
              {[{ val: '', lbl: 'No Change' }, { val: '1', lbl: 'Yes, Attending' }, { val: '0', lbl: 'Not Attending' }].map(({ val, lbl }) => (
                <button key={val} type="button"
                  onClick={() => setForm((p) => ({ ...p, is_attending_classes: val, attending_since: val === '1' ? p.attending_since : '' }))}
                  className={`flex-1 py-2 text-xs font-medium rounded-xl border transition-colors ${form.is_attending_classes === val ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {showAttendingSince && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Attending Since</label>
              <input type="date" value={form.attending_since} onChange={(e) => setForm((p) => ({ ...p, attending_since: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent" />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors">
            {saving && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            Save Follow-up
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inquiry Row (compact card inside an assignee group) ──────────────────────

function InquiryRow({ inquiry, isAdmin, onDeleted, onFollowupAdded }) {
  const [expanded, setExpanded] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isReactivated = inquiry.status === 'reactivated';
  const _td = new Date();
  const todayStr = `${_td.getFullYear()}-${String(_td.getMonth() + 1).padStart(2, '0')}-${String(_td.getDate()).padStart(2, '0')}`;
  const reminderDue =
    inquiry.next_reminder_date &&
    toLocalDateStr(inquiry.next_reminder_date) === todayStr;

  const loadFollowups = useCallback(async () => {
    setLoadingFollowups(true);
    try {
      const { data } = await axiosInstance.get(`/admin/inactivated-inquiry/${inquiry.inquiry_id}/followups`);
      setFollowups(Array.isArray(data) ? data : []);
    } catch {
      setFollowups([]);
    } finally {
      setLoadingFollowups(false);
    }
  }, [inquiry.inquiry_id]);

  const handleExpand = () => {
    if (!expanded) loadFollowups();
    setExpanded((v) => !v);
  };

  const handleFollowupAdded = () => {
    loadFollowups();
    onFollowupAdded();
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Inquiry?',
      html: `Delete inquiry for <strong>${inquiry.full_name}</strong>?<br/>This will also remove all follow-up records and cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/admin/inactivated-inquiry/${inquiry.inquiry_id}`);
      onDeleted();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: err?.response?.data?.message || 'Failed to delete inquiry.' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${isReactivated ? 'border-emerald-200 bg-emerald-50/30' : reminderDue ? 'border-orange-200 bg-orange-50/20' : 'border-gray-100 bg-white'}`}>
      {/* Row Header */}
      <div className="px-4 py-3 flex items-center gap-3">
        <Avatar src={inquiry.image_path} name={inquiry.full_name} gender={inquiry.gender} size={38} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{inquiry.full_name}</span>
            <StatusBadge status={inquiry.status} />
            {inquiry.is_attending_classes === 1 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 font-medium">
                <MdSchool size={11} /> Attending
              </span>
            )}
            {reminderDue && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 border border-orange-200 text-orange-700 font-medium">
                <MdCalendarToday size={11} /> Reminder Today
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-xs text-gray-500">
            <span className="text-indigo-600 font-medium">{inquiry.registration_number}</span>
            <span className="truncate">{inquiry.course_name} · {inquiry.batch_name}</span>
            {inquiry.telephone && (
              <span className="flex items-center gap-1">
                <MdPhone size={11} className="text-gray-400" />{inquiry.telephone}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-xs text-gray-500">
            {inquiry.next_reminder_date && (
              <span className="flex items-center gap-1">
                <MdCalendarToday size={11} className="text-gray-400" />
                Reminder: <span className="font-medium text-gray-700">{fmtDate(inquiry.next_reminder_date)}</span>
              </span>
            )}
            {inquiry.planned_rejoin_date && (
              <span className="flex items-center gap-1 text-violet-600 font-medium">
                <MdCheckCircle size={11} />
                Plans to rejoin {fmtDate(inquiry.planned_rejoin_date)}
              </span>
            )}
            {inquiry.reactivated_date && (
              <span className="flex items-center gap-1 text-emerald-600 font-medium">
                <MdCheckCircle size={11} />
                Reactivated {fmtDate(inquiry.reactivated_date)}
              </span>
            )}
            {inquiry.inquiry_goal && (
              <span>Goal: <span className="text-gray-700 capitalize">{inquiry.inquiry_goal}</span></span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setShowFollowupModal(true)}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
          >
            <MdAdd size={13} /> Follow-up
          </button>
          {isAdmin && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 disabled:opacity-40 transition-colors"
              title="Delete inquiry (admin only)"
            >
              {deleting
                ? <span className="w-3.5 h-3.5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin block" />
                : <MdDelete size={16} />}
            </button>
          )}
          <button
            onClick={handleExpand}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title={expanded ? 'Collapse' : 'View history'}
          >
            {expanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
          </button>
        </div>
      </div>

      {inquiry.inactivation_reason && (
        <div className="px-4 pb-3">
          <p className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-1.5 leading-relaxed">
            <span className="font-medium text-gray-500">Reason: </span>{inquiry.inactivation_reason}
          </p>
        </div>
      )}

      {/* Follow-up History Panel */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/70 px-4 py-3">
          <div className="flex items-center gap-2 mb-3">
            <MdHistory size={14} className="text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Follow-up History</span>
          </div>
          {loadingFollowups && (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
              <span className="w-3.5 h-3.5 border-2 border-gray-200 border-t-indigo-400 rounded-full animate-spin" />
              Loading...
            </div>
          )}
          {!loadingFollowups && followups.length === 0 && (
            <p className="text-xs text-gray-400 italic py-1">No follow-ups recorded yet.</p>
          )}
          {!loadingFollowups && followups.length > 0 && (
            <div className="space-y-2">
              {followups.map((fu, idx) => (
                <div key={fu.followup_id} className="relative pl-4">
                  {idx < followups.length - 1 && (
                    <div className="absolute left-1 top-4 bottom-0 w-px bg-gray-200" />
                  )}
                  <div className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full border-2 border-indigo-300 bg-white" />
                  <div className="bg-white border border-gray-100 rounded-xl px-3 py-2.5 shadow-sm">
                    <div className="flex items-center justify-between flex-wrap gap-1 mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-700">{fu.created_by_name || 'Staff'}</span>
                        {fu.status_update && <StatusBadge status={fu.status_update} />}
                      </div>
                      <span className="text-xs text-gray-400">{fmtDatetime(fu.created_date)}</span>
                    </div>
                    <p className="text-xs text-gray-700 leading-relaxed">{fu.comment}</p>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                      {fu.planned_rejoin_date && (
                        <span className="text-violet-600 font-medium flex items-center gap-1">
                          <MdCalendarToday size={11} /> Rejoin: {fmtDate(fu.planned_rejoin_date)}
                        </span>
                      )}
                      {fu.next_reminder_date && (
                        <span className="flex items-center gap-1">
                          <MdCalendarToday size={11} /> Next: {fmtDate(fu.next_reminder_date)}
                        </span>
                      )}
                      {fu.is_attending_classes === 1 && (
                        <span className="text-teal-600 flex items-center gap-1">
                          <MdSchool size={11} /> Attending since {fmtDate(fu.attending_since)}
                        </span>
                      )}
                      {fu.is_attending_classes === 0 && (
                        <span className="text-orange-500 flex items-center gap-1">
                          <MdWarning size={11} /> Stopped attending
                        </span>
                      )}
                      {fu.next_reminder_comment && (
                        <span className="flex items-center gap-1">
                          <MdComment size={11} /> {fu.next_reminder_comment}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showFollowupModal && (
        <AddFollowupModal
          inquiry={inquiry}
          onClose={() => setShowFollowupModal(false)}
          onAdded={handleFollowupAdded}
        />
      )}
    </div>
  );
}

// ─── Assignee Group Card ──────────────────────────────────────────────────────

function AssigneeGroup({ staffName, staffId, inquiries, isAdmin, onRefresh }) {
  const [collapsed, setCollapsed] = useState(false);
  const _td = new Date();
  const todayStr = `${_td.getFullYear()}-${String(_td.getMonth() + 1).padStart(2, '0')}-${String(_td.getDate()).padStart(2, '0')}`;

  const remindersToday = inquiries.filter(
    (i) => i.next_reminder_date && toLocalDateStr(i.next_reminder_date) === todayStr
  ).length;
  const activeCount = inquiries.filter((i) => i.status !== 'reactivated' && i.status !== 'permanently inactive').length;
  const reactivatedCount = inquiries.filter((i) => i.status === 'reactivated').length;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      {/* Group Header */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50/80 transition-colors text-left"
      >
        <StaffAvatar name={staffName} size={44} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{staffName}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 font-medium">
              {inquiries.length} {inquiries.length === 1 ? 'inquiry' : 'inquiries'}
            </span>
            {remindersToday > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-600 font-medium">
                <MdCalendarToday className="inline mr-0.5" size={11} />
                {remindersToday} due today
              </span>
            )}
            {reactivatedCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 font-medium">
                {reactivatedCount} reactivated
              </span>
            )}
          </div>
          <div className="flex gap-4 mt-1 text-xs text-gray-500">
            <span>{activeCount} active · {reactivatedCount} reactivated · {inquiries.length - activeCount - reactivatedCount} other</span>
          </div>
        </div>
        <div className="flex-shrink-0 text-gray-400">
          {collapsed ? <MdExpandMore size={22} /> : <MdExpandLess size={22} />}
        </div>
      </button>

      {/* Inquiries List */}
      {!collapsed && (
        <div className="border-t border-gray-100 bg-gray-50/40 px-5 py-4 space-y-3">
          {inquiries.map((inquiry) => (
            <InquiryRow
              key={inquiry.inquiry_id}
              inquiry={inquiry}
              isAdmin={isAdmin}
              onDeleted={onRefresh}
              onFollowupAdded={onRefresh}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function InactivatedInquiryByAssignee() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [reminderTodayOnly, setReminderTodayOnly] = useState(false);

  const _user = AuthService.getUser();
  const _role = _user?.superAdmin?.staff?.role || _user?.superAdmin?.role;
  const isAdmin = Boolean(_user?.superAdmin?.superAdminId) || _role === 'Admin' || _role === 'Administrator';
  const currentStaffId = _user?.superAdmin?.staff?.staffId;

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/admin/inactivated-inquiry');
      setInquiries(Array.isArray(data) ? data : []);
    } catch {
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const _td = new Date();
  const todayStr = `${_td.getFullYear()}-${String(_td.getMonth() + 1).padStart(2, '0')}-${String(_td.getDate()).padStart(2, '0')}`;

  // Group only inquiries assigned to the current user
  const groupedByAssignee = useMemo(() => {
    let assigned = inquiries.filter((i) => i.assigned_to === currentStaffId);

    if (reminderTodayOnly) {
      assigned = assigned.filter(
        (i) => i.next_reminder_date && toLocalDateStr(i.next_reminder_date) === todayStr
      );
    }

    let filtered = assigned;
    if (searchQ.trim()) {
      const q = searchQ.trim().toLowerCase();
      filtered = assigned.filter(
        (i) =>
          String(i.full_name || '').toLowerCase().includes(q) ||
          String(i.registration_number || '').toLowerCase().includes(q) ||
          String(i.assigned_to_name || '').toLowerCase().includes(q) ||
          String(i.course_name || '').toLowerCase().includes(q) ||
          String(i.batch_name || '').toLowerCase().includes(q)
      );
    }

    const map = new Map();
    filtered.forEach((inquiry) => {
      const key = inquiry.assigned_to;
      if (!map.has(key)) {
        map.set(key, {
          staffId: key,
          staffName: inquiry.assigned_to_name || `Staff #${key}`,
          inquiries: [],
        });
      }
      map.get(key).inquiries.push(inquiry);
    });

    // Sort groups by staff name
    return Array.from(map.values()).sort((a, b) =>
      a.staffName.localeCompare(b.staffName)
    );
  }, [inquiries, searchQ, reminderTodayOnly, todayStr, currentStaffId]);

  const myInquiries = inquiries.filter((i) => i.assigned_to === currentStaffId);
  const totalInFollowup = myInquiries.filter((i) => i.status === 'in followup').length;
  const totalReactivated = myInquiries.filter((i) => i.status === 'reactivated').length;
  const totalRemindersToday = myInquiries.filter(
    (i) => i.next_reminder_date && toLocalDateStr(i.next_reminder_date) === todayStr
  ).length;

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50/50">
        {/* Page Header */}
        <div className="bg-white border-b border-gray-100 px-6 py-5">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Inactivated List Followup</h1>
              <p className="text-sm text-gray-500 mt-0.5">View inactivated student inquiries grouped by assigned staff member</p>
            </div>
            <button
              onClick={loadInquiries}
              className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-colors"
              title="Refresh"
            >
              <MdRefresh size={18} />
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
          {/* Stats Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'My Total', value: myInquiries.length, color: 'text-gray-900', bg: 'bg-white' },
              { label: 'In Follow-up', value: totalInFollowup, color: 'text-sky-600', bg: 'bg-sky-50' },
              { label: 'Reactivated', value: totalReactivated, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Reminders Today', value: totalRemindersToday, color: 'text-orange-600', bg: 'bg-orange-50' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className={`${bg} border border-gray-100 rounded-xl px-4 py-3 text-center shadow-sm`}>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Search + Reminder Today */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search by student name, registration, staff, course, batch..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white"
              />
            </div>
            <button
              onClick={() => setReminderTodayOnly((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors flex-shrink-0 ${
                reminderTodayOnly
                  ? 'bg-orange-500 border-orange-500 text-white shadow-sm'
                  : 'border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 bg-white'
              }`}
              title="Show only assignees with a reminder due today"
            >
              <MdCalendarToday size={16} />
              Reminder Today
              {totalRemindersToday > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                  reminderTodayOnly ? 'bg-white text-orange-600' : 'bg-orange-100 text-orange-600'
                }`}>
                  {totalRemindersToday}
                </span>
              )}
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
              <span className="w-8 h-8 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : groupedByAssignee.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                <MdGroup size={32} className="text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-500">
                {myInquiries.length === 0
                  ? 'No inquiries are assigned to you'
                  : 'No results match your search'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                Showing <span className="font-semibold text-gray-700">{groupedByAssignee.length}</span>{' '}
                {groupedByAssignee.length === 1 ? 'assignee' : 'assignees'} with{' '}
                <span className="font-semibold text-gray-700">
                  {groupedByAssignee.reduce((s, g) => s + g.inquiries.length, 0)}
                </span>{' '}
                {groupedByAssignee.reduce((s, g) => s + g.inquiries.length, 0) === 1 ? 'inquiry' : 'inquiries'}
              </p>
              {groupedByAssignee.map((group) => (
                <AssigneeGroup
                  key={group.staffId}
                  staffId={group.staffId}
                  staffName={group.staffName}
                  inquiries={group.inquiries}
                  isAdmin={isAdmin}
                  onRefresh={loadInquiries}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
